import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";

import City from "../../entities/City";
import Country from "../../entities/Country";
import State, { County } from "../../entities/State";
import ZipCode from "../../entities/ZipCode";
import i18n from "../../i18n";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import ResponseBase from "../types/ResponseBase";
import SliceCollectionBase from "../types/SliceCollectionBase";
import { authHeader } from "../users/auth";
import { extractCollection, extractFirst } from "./normalizers";


//TODO: AS OF 07.2024 this slice is not in use.

// RESPONSE DECLARATIONS //
interface CountryResponse extends ResponseBase {
    Code: string;
    Name: string;
}

interface StateResponse extends ResponseBase {
    CountryId: number;
    Name: string;
}
interface CountyResponse extends ResponseBase {
    CountryId: number;
    StateId: number;
    Name: string;
}
interface CityResponse extends ResponseBase {
    CountryId: number;
    CountyId: number;
    Name: string;
    StateId: number;
}

interface ZipCodeResponse extends ResponseBase {
    CityId: number;
    Code: string;
    CountryId: number;
    CountyId: number;
    Name: string;
    StateId: number;
}

type ClassificatorResponse<T> = T[] | { value: T[] };

// SLICE //
interface SliceState {
    countries: SliceCollectionBase<Country>;
    states: SliceCollectionBase<State>;
    counties: SliceCollectionBase<County>;
    cities: SliceCollectionBase<City>;
    zipCodes: SliceCollectionBase<ZipCode>;
}

const initialState: SliceState = {
    countries: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    states: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    counties: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    cities: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    zipCodes: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    }
}

const slice = createSlice({
    name: "address",
    initialState,
    reducers: {
        // COUNTRIES
        countriesRequested: (address) => {
            address.countries.loading = true;
            address.countries.error = null;
        },
        countriesReceived: (address, action: PayloadAction<unknown>) => {
            address.countries.list = countriesSchema(extractCollection<CountryResponse>(action.payload));
            address.countries.loading = false;
            address.countries.lastFetch = Date.now();
        },
        countriesRequestFailed: (address, action: PayloadAction<ApiCallFailedPayload>) => {
            address.countries.loading = false;
            address.countries.error = action.payload;
        },

        // STATES
        statesRequested: (address) => {
            address.states.loading = true;
            address.states.error = null;
        },
        statesReceived: (address, action: PayloadAction<unknown>) => {
            address.states.list = statesSchema(extractCollection<StateResponse>(action.payload));
            address.states.loading = false;
            address.states.lastFetch = Date.now();
        },
        statesRequestFailed: (address, action: PayloadAction<ApiCallFailedPayload>) => {
            address.states.loading = false;
            address.states.error = action.payload;
        },

        // Counties
        countiesRequested: (address) => {
            address.counties.loading = true;
            address.counties.error = null;
        },
        countiesReceived: (address, action: PayloadAction<unknown>) => {
            address.counties.list = extractCollection<CountyResponse>(action.payload);
            address.counties.loading = false;
            address.counties.lastFetch = Date.now();
        },
        countiesRequestFailed: (address, action: PayloadAction<ApiCallFailedPayload>) => {
            address.counties.loading = false;
            address.counties.error = action.payload;
        },

        // CITIES
        citiesRequested: (address) => {
            address.cities.loading = true;
            address.cities.error = null;
        },
        citiesReceived: (address, action: PayloadAction<unknown>) => {
            address.cities.list = citiesSchema(extractCollection<CityResponse>(action.payload));
            address.cities.loading = false;
            address.cities.lastFetch = Date.now();
        },
        citiesRequestFailed: (address, action: PayloadAction<ApiCallFailedPayload>) => {
            address.cities.loading = false;
            address.cities.error = action.payload;
        },

        // ZIPCODES
        zipCodesRequested: (address) => {
            address.zipCodes.loading = true;
            address.zipCodes.error = null;
        },
        zipCodesReceived: (address, action: PayloadAction<unknown>) => {
            address.zipCodes.list = zipCodesSchema(extractCollection<ZipCodeResponse>(action.payload));
            address.zipCodes.loading = false;
            address.zipCodes.lastFetch = Date.now();
        },
        zipCodesRequestFailed: (address, action: PayloadAction<ApiCallFailedPayload>) => {
            address.zipCodes.loading = false;
            address.zipCodes.error = action.payload;
        }
    }
});

const {
    countriesRequested,
    countriesReceived,
    countriesRequestFailed,

    statesRequested,
    statesReceived,
    statesRequestFailed,

    countiesRequested,
    countiesReceived,
    countiesRequestFailed,

    citiesRequested,
    citiesReceived,
    citiesRequestFailed,

    zipCodesRequested,
    zipCodesReceived,
    zipCodesRequestFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getCountries = () => async (dispatch: AppDispatch, getState: () => AppState) => {
    const { lastFetch, loading } = getState().entities.address.countries;

    if (loading) {
        return;
    }

    const diffInMinutes = moment().diff(moment(lastFetch), "minutes");
    if (diffInMinutes < parseInt((window._env_.REACT_APP_COMMON_REQUEST_INTERVAL || import.meta.env.REACT_APP_COMMON_REQUEST_INTERVAL)!, 10)) {
        return;
    }

    const url = "/api/classificator/countries";

    dispatch({ type: statesReceived.type, payload: { value: [] } });
    dispatch({ type: citiesReceived.type, payload: { value: [] } });
    dispatch({ type: zipCodesReceived.type, payload: { value: [] } });

    return await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: countriesRequested.type,
            onSuccess: countriesReceived.type,
            onError: countriesRequestFailed.type
        })
    );
};

export const getStates = (countryId: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!countryId || getState().entities.address.states.loading) {
        return;
    }

    const url = countryId === 0
        ? "/api/classificator/states"
        : `/api/classificator/states?countryId=${countryId}`;

    //dispatch({ type: citiesReceived.type, payload: { value: [] } });
    //dispatch({ type: zipCodesReceived.type, payload: { value: [] } });

    return await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: statesRequested.type,
            onSuccess: statesReceived.type,
            onError: statesRequestFailed.type
        })
    );
};

export const getCounties = (countryId: number, stateId?: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!countryId || getState().entities.address.counties.loading) {
        return;
    }
    // TODO: fix to filter athru both
    //const filter = stateId ? `filter=StateId eq ${stateId}` : `filter=CountryId eq ${countryId}`;

    const url = countryId === 0
        ? "/api/classificator/counties"
        : `/api/classificator/counties?countryId=${countryId}`;

    return await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: countiesRequested.type,
            onSuccess: countiesReceived.type,
            onError: countiesRequestFailed.type
        })
    );
};

export const getCities = (countryId: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!countryId || getState().entities.address.cities.loading) {
        return;
    }

    const url = countryId === 0
        ? "/api/classificator/cities"
        : `/api/classificator/cities?countryId=${countryId}`;

    // dispatch({ type: zipCodesReceived.type, payload: { value: [] } });

    return await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: citiesRequested.type,
            onSuccess: citiesReceived.type,
            onError: citiesRequestFailed.type
        })
    );
};

export const getCitiesByCounty = (countyId: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!countyId || getState().entities.address.cities.loading) {
        return;
    }
    const url = countyId === 0
        ? "/api/classificator/cities"
        : `/api/classificator/cities?countyId=${countyId}`;

    return await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: citiesRequested.type,
            onSuccess: citiesReceived.type,
            onError: citiesRequestFailed.type
        })
    );
};

export const getZipCodes = (countryId: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!countryId || getState().entities.address.zipCodes.loading) {
        return;
    }

    const url = countryId === 0
        ? "/api/classificator/zip-codes"
        : `/api/classificator/zip-codes?countryId=${countryId}`;

    return await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: zipCodesRequested.type,
            onSuccess: zipCodesReceived.type,
            onError: zipCodesRequestFailed.type
        })
    );
};

// NORMALIZATION //
const countriesSchema = (payload: CountryResponse[]): Country[] => {
    if (!payload || payload.length === 0) {
        return [];
    }
    return payload.map((resp: CountryResponse): Country => (
        {
            Code: resp.Code,
            Id: resp.Id,
            Name: resp.Name
        }
    )).sort((a, b) => a.Name?.localeCompare(b.Name));
};

const statesSchema = (payload: StateResponse[]): State[] => {
    if (!payload || payload.length === 0) {
        return [];
    }
    return payload.map((resp: StateResponse): State => (
        {
            CountryId: resp.CountryId,
            Id: resp.Id,
            Name: resp.Name
        }
    )).sort((a, b) => a.Name?.localeCompare(b.Name));
};

const countiesSchema = (payload: CountyResponse[]): County[] => {
    if (!payload || payload.length === 0) {
        return [];
    }
    return payload.map((resp: CountyResponse): County => (
        {
            CountryId: resp.CountryId,
            Id: resp.Id,
            Name: resp.Name,
            StateId: resp.StateId
        }
    )).sort((a, b) => a.Name?.localeCompare(b.Name));
};

const citiesSchema = (payload: CityResponse[]): City[] => {
    if (!payload || payload.length === 0) {
        return [];
    }
    return payload.map((resp: CityResponse): City => (
        {
            CountryId: resp.CountryId,
            CountyId: resp.CountyId,
            Id: resp.Id,
            Name: resp.Name,
            StateId: resp.StateId
        }
    )).sort((a, b) => a.Name?.localeCompare(b.Name));
};

const zipCodesSchema = (payload: ZipCodeResponse[]): ZipCode[] => {
    if (!payload || payload.length === 0) {
        return [];
    }
    return payload.map((resp: ZipCodeResponse): ZipCode => (
        {
            CityId: resp.CityId,
            Code: resp.Code,
            CountryId: resp.CountryId,
            CountyId: resp.CountyId,
            Id: resp.Id,
            Name: resp.Name,
            StateId: resp.StateId
        }
    )).sort((a, b) => a.Code?.localeCompare(b.Code));
};